import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Label } from "@/components/ui/label";
import { Zap, Gauge, Lock } from "lucide-react";
import { usePlanRestrictions } from "@/hooks/usePlanRestrictions";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

interface ModelSelectorProps {
  model: string;
  onModelChange: (value: string) => void;
}

const MODEL_DISPLAY_NAMES: Record<string, string> = {
  'gpt-4o': 'Extended',
  'gpt-4o-mini': 'Efficient'
};

const DISPLAY_TO_INTERNAL_NAMES: Record<string, string> = {
  'Extended': 'gpt-4o',
  'Efficient': 'gpt-4o-mini'
};

export const ModelSelector = ({ model, onModelChange }: ModelSelectorProps) => {
  const displayName = MODEL_DISPLAY_NAMES[model] || model;

  // Fetch current plan directly to ensure we have the latest data
  const { data: currentPlan } = useQuery({
    queryKey: ['currentPlan'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) return null;

      const { data, error } = await supabase.rpc('get_user_plan', {
        p_user_id: session.user.id
      });
      
      if (error) throw error;
      return data[0];
    },
  });

  const canUseExtendedModel = currentPlan?.allows_extended_model ?? false;

  const handleModelChange = (displayName: string) => {
    const internalName = DISPLAY_TO_INTERNAL_NAMES[displayName] || displayName;
    
    if (internalName === 'gpt-4o' && !canUseExtendedModel) {
      toast.error(
        "Extended model not available",
        {
          description: "Upgrade your plan to use the extended model",
          action: {
            label: "Upgrade",
            onClick: () => window.location.href = "/settings?tab=tokens"
          }
        }
      );
      return;
    }
    
    console.log('[ModelSelector] Selected model:', { displayName, internalName });
    onModelChange(internalName);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>Model</Label>
        {!canUseExtendedModel && (
          <Badge variant="secondary" className="text-xs">
            <Lock className="h-3 w-3 mr-1" />
            Extended model requires upgrade
          </Badge>
        )}
      </div>
      <ToggleGroup 
        type="single" 
        value={displayName} 
        onValueChange={handleModelChange}
        className="justify-start"
      >
        <ToggleGroupItem value="Efficient" aria-label="Efficient model">
          <Gauge className="h-4 w-4 mr-2" />
          Efficient
        </ToggleGroupItem>
        <ToggleGroupItem 
          value="Extended" 
          aria-label="Extended model"
          disabled={!canUseExtendedModel}
          className={!canUseExtendedModel ? "opacity-50 cursor-not-allowed" : ""}
        >
          <Zap className="h-4 w-4 mr-2" />
          Extended
          {!canUseExtendedModel && <Lock className="h-3 w-3 ml-2" />}
        </ToggleGroupItem>
      </ToggleGroup>
    </div>
  );
};